import { renderPopin } from './popin-renderer.js'
import { renderLucideIcon } from '../utils/lucide-icons.js'

const QUOTE_DRAFT_PROMPT = 'Décrivez-moi ce que vous souhaitez proposer à votre interlocuteur'

function normalizeQuoteDraftUi(result) {
  if (!result || typeof result !== 'object') return null

  var ui = result.ui || result.quoteDraftUi || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'quote-draft') return null

  var draft = normalizeDraft(ui.draft)
  if (!draft) return null

  return {
    type: 'quote-draft',
    title: String(ui.title || 'Devis en préparation').trim(),
    message: String(ui.message || '').trim(),
    clarificationQuestion: String(ui.clarificationQuestion || '').trim(),
    pending: ui.pending === true,
    submissionBlocked: ui.submissionBlocked !== false,
    submissionResult: normalizeSubmissionResult(ui.submissionResult),
    dialogue: normalizeDialogue(ui.dialogue),
    draft: draft,
    labels: normalizeLabels(ui.labels)
  }
}

function renderQuoteDraftUi(ui) {
  var header = (
    '<div class="wc-quote-draft-headline">' +
      '<div class="wc-quote-draft-capability">' + renderLucideIcon('fileText') + '<span>' + escapeHtml(ui.labels.capability) + '</span></div>' +
      '<strong>' + escapeHtml(ui.title) + '</strong>' +
    '</div>'
  )

  return renderPopin({
    title: ui.title,
    ariaLabel: 'Preparation de devis',
    className: 'wc-quote-draft-popin',
    dialogClassName: 'wc-quote-draft-dialog',
    header: header,
    body:
      '<div class="wc-quote-draft-layout" data-ds-view="quote-draft">' +
        renderDialoguePane(ui) +
        renderDraftPane(ui) +
      '</div>'
  })
}

function bindQuoteDraftUi(el, ui, handlers) {
  if (!el || !ui) return
  handlers = handlers || {}
  Array.prototype.slice.call(el.querySelectorAll('[data-popin-close], [data-quote-draft-cancel]')).forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault()
      var root = button.closest ? button.closest('.wc-popin') : null
      if (root && root.parentNode) root.parentNode.removeChild(root)
      if (handlers && typeof handlers.onCancel === 'function') handlers.onCancel(ui)
    })
  })
  var input = el.querySelector('.wc-quote-draft-compose-input')
  var submit = el.querySelector('.wc-quote-draft-compose-send')
  var mic = el.querySelector('.wc-quote-draft-compose-mic')
  function submitInput(event) {
    if (event) event.preventDefault()
    var value = String(input && input.value || '').trim()
    if (!value) return
    if (typeof handlers.onSubmit === 'function') handlers.onSubmit(value, ui)
    if (input) input.value = ''
  }
  if (submit) submit.addEventListener('click', submitInput)
  if (mic) {
    mic.addEventListener('click', function (event) {
      event.preventDefault()
      if (typeof handlers.onDictate === 'function') handlers.onDictate(input, mic, ui)
    })
  }
  Array.prototype.slice.call(el.querySelectorAll('[data-quote-draft-choice]')).forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault()
      var value = String(button.getAttribute('data-quote-draft-choice') || '').trim()
      if (!value) return
      if (typeof handlers.onSubmit === 'function') handlers.onSubmit(value, ui)
    })
  })
  var draftSubmit = el.querySelector('.wc-quote-draft-submit')
  if (draftSubmit) {
    draftSubmit.addEventListener('click', function (event) {
      event.preventDefault()
      if (ui.submissionBlocked) return
      if (typeof handlers.onSubmitQuote === 'function') handlers.onSubmitQuote(ui)
    })
  }
  if (input) {
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        submitInput(event)
      }
    })
  }
}

function renderDialoguePane(ui) {
  var prompt = QUOTE_DRAFT_PROMPT
  var dialogue = ui.dialogue.slice()
  if (ui.pending) dialogue = removePendingAssistantEcho(dialogue)
  if (!ui.pending && !dialogue.length && ui.message && ui.message !== QUOTE_DRAFT_PROMPT) {
    dialogue.push({ role: 'assistant', text: ui.message })
  }
  if (!ui.pending && ui.clarificationQuestion && !dialogue.some(function (entry) { return entry.role === 'assistant' && entry.text === ui.clarificationQuestion })) {
    dialogue.push({ role: 'assistant', text: ui.clarificationQuestion })
  }
  var messages = dialogue.map(renderDialogueMessage).join('') + (ui.pending ? renderDialoguePending() : '')

  return (
    '<section class="wc-quote-draft-dialogue" aria-label="' + escapeAttributeValue(ui.labels.dialogueTitle) + '">' +
      '<div class="wc-quote-draft-thread" aria-live="polite">' + messages + '</div>' +
      '<div class="wc-quote-draft-compose">' +
        '<textarea class="wc-quote-draft-compose-input" rows="2" aria-label="' + escapeAttributeValue(prompt) + '" placeholder="' + escapeAttributeValue(prompt) + '"></textarea>' +
        '<button type="button" class="wc-quote-draft-compose-mic" aria-label="Dicter la demande" aria-pressed="false" title="Dicter la demande">' + renderLucideIcon('mic') + '</button>' +
        '<button type="button" class="wc-quote-draft-compose-send" aria-label="Envoyer la demande">' + renderLucideIcon('send') + '</button>' +
      '</div>' +
    '</section>'
  )
}

function removePendingAssistantEcho(dialogue) {
  var lastUserEntry = null
  dialogue.forEach(function (entry) {
    if (entry.role === 'user') lastUserEntry = entry
  })
  var lastUserText = lastUserEntry ? lastUserEntry.text : ''
  if (!lastUserText) return dialogue
  return dialogue.filter(function (entry) {
    return !(entry.role === 'assistant' && entry.text === lastUserText)
  })
}

function renderDialoguePending() {
  return (
    '<div class="wc-quote-draft-dialogue-message wc-quote-draft-dialogue-message-assistant wc-quote-draft-dialogue-message-pending" aria-label="Chargement en cours">' +
      '<span class="wc-quote-draft-dialogue-author">Assistant IA</span>' +
      '<div class="wc-quote-draft-bubble wc-quote-draft-typing">' +
        '<span class="wc-typing-dot"></span>' +
        '<span class="wc-typing-dot"></span>' +
        '<span class="wc-typing-dot"></span>' +
      '</div>' +
    '</div>'
  )
}

function renderDialogueMessage(entry) {
  var role = entry.role === 'user' ? 'user' : 'assistant'
  var label = role === 'user' ? 'Vous' : 'Assistant IA'
  var bubble = role === 'assistant'
    ? renderAssistantDialogueBubble(entry.text)
    : '<div class="wc-quote-draft-bubble">' + escapeHtml(entry.text) + '</div>'
  return (
    '<div class="wc-quote-draft-dialogue-message wc-quote-draft-dialogue-message-' + role + '">' +
      '<span class="wc-quote-draft-dialogue-author">' + escapeHtml(label) + '</span>' +
      bubble +
    '</div>'
  )
}

function renderAssistantDialogueBubble(text) {
  var productChoices = parseProductChoiceQuestion(text)
  if (!productChoices) return '<div class="wc-quote-draft-bubble">' + escapeHtml(text) + '</div>'
  return (
    '<div class="wc-quote-draft-bubble">' +
      '<span>' + escapeHtml(productChoices.question) + '</span>' +
      '<div class="wc-quote-draft-choice-chips">' +
        productChoices.choices.map(function (choice) {
          return (
            '<button type="button" class="wc-quote-draft-choice-chip" data-quote-draft-choice="' + escapeAttributeValue(choice.value) + '">' +
              escapeHtml(choice.label) +
            '</button>'
          )
        }).join('') +
      '</div>' +
    '</div>'
  )
}

function parseProductChoiceQuestion(text) {
  var lines = String(text || '').split(/\r?\n/).map(function (line) { return line.trim() }).filter(Boolean)
  if (lines.length < 2 || !/^Plusieurs produits correspondent\./i.test(lines[0])) return null
  var choices = lines.slice(1).map(function (line) {
    var label = line.replace(/^-\s*/, '').trim()
    if (!label) return null
    var referenceMatch = label.match(/\(([^()]+)\)\s*$/)
    return {
      label: label,
      value: referenceMatch ? referenceMatch[1].trim() : label
    }
  }).filter(Boolean)
  if (!choices.length) return null
  return {
    question: lines[0],
    choices: choices
  }
}

function renderDraftPane(ui) {
  var draft = ui.draft
  var customer = draft.customer ? renderContextRow(ui.labels.customer, draft.customer) : ''
  var deal = draft.deal ? renderContextRow(ui.labels.deal, draft.deal) : ''
  var context = customer || deal
    ? customer + deal
    : '<div class="wc-quote-draft-context-empty">' + renderLucideIcon('triangleAlert') + '<span>' + escapeHtml(ui.labels.noContext) + '</span></div>'
  var sections = draft.sections.length
    ? draft.sections.map(function (section) {
      var lines = section.lines.length
        ? section.lines.map(renderDraftLine).join('')
        : '<div class="wc-quote-draft-empty-lines">' + escapeHtml(ui.labels.emptyDraft) + '</div>'
      var sectionTotal = calculateSectionTotal(section)
      var sectionTotalHtml = section.lines.length
        ? renderAmountRow({
            className: 'wc-quote-draft-section-total',
            label: ui.labels.sectionTotal,
            amount: sectionTotal,
            unavailableLabel: ui.labels.amountUnavailable
          })
        : ''
      return (
        '<section class="wc-quote-draft-section">' +
          '<div class="wc-quote-draft-section-head">' +
            '<div class="wc-quote-draft-section-title">' + escapeHtml(section.title) + '</div>' +
          '</div>' +
          lines +
          sectionTotalHtml +
        '</section>'
      )
    }).join('')
    : '<div class="wc-quote-draft-empty-lines">' + escapeHtml(ui.labels.emptyDraft) + '</div>'
  var total = normalizeAmount({
    value: draft.totals && draft.totals.amountExcludingTax,
    currency: draft.totals && draft.totals.currency
  })
  var submitDisabled = ui.submissionBlocked ? ' disabled aria-disabled="true"' : ' aria-disabled="false"'

  return (
    '<section class="wc-quote-draft-preview" aria-label="' + escapeAttributeValue(ui.labels.draftTitle) + '">' +
      '<article class="wc-quote-draft-paper">' +
        '<div class="wc-quote-draft-paper-head">' +
          '<div>' +
            '<span class="wc-quote-draft-kicker">' + escapeHtml(ui.labels.draftTitle) + '</span>' +
            '<h2>' + escapeHtml(ui.title) + '</h2>' +
          '</div>' +
          '<span class="wc-quote-draft-status">Brouillon</span>' +
        '</div>' +
        '<div class="wc-quote-draft-context">' + context + '</div>' +
        sections +
        renderAmountRow({
          className: 'wc-quote-draft-total',
          label: ui.labels.total,
          amount: total,
          unavailableLabel: ui.labels.totalUnavailable
        }) +
        renderSubmissionResult(ui.submissionResult) +
      '</article>' +
      '<div class="wc-quote-draft-actions">' +
        '<button type="button" class="wc-quote-draft-cancel" data-quote-draft-cancel>' + renderLucideIcon('x') + '<span>' + escapeHtml(ui.labels.cancel) + '</span></button>' +
        '<button type="button" class="wc-quote-draft-submit"' + submitDisabled + '>' + renderLucideIcon('send') + '<span>' + escapeHtml(ui.labels.submit) + '</span></button>' +
      '</div>' +
    '</section>'
  )
}

function renderSubmissionResult(result) {
  if (!result) return ''
  var link = result.remoteUrl
    ? ' <a href="' + escapeAttributeValue(result.remoteUrl) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(result.remoteUrl) + '</a>'
    : ''
  return '<div class="wc-quote-draft-submission wc-quote-draft-submission-' + escapeAttributeValue(result.status) + '">' +
    renderLucideIcon(result.status === 'succeeded' ? 'circleCheck' : 'triangleAlert') +
    '<span>' + escapeHtml(result.message) + link + '</span>' +
  '</div>'
}

function renderDraftLine(line) {
  var reference = line.product && line.product.reference
    ? '<span class="wc-quote-draft-line-ref">' + escapeHtml(line.product.reference) + '</span>'
    : ''
  var productLabel = line.product && line.product.label
    ? '<span class="wc-quote-draft-line-product">' + escapeHtml(line.product.label) + '</span>'
    : ''
  var productMeta = productLabel || reference
    ? '<span class="wc-quote-draft-line-product-meta">' + productLabel + reference + '</span>'
    : ''
  var comment = line.comment
    ? '<span class="wc-quote-draft-line-comment">' + escapeHtml(line.comment) + '</span>'
    : ''
  var unitAmount = line.unitAmountExcludingTax ? formatAmount(line.unitAmountExcludingTax.value, line.unitAmountExcludingTax.currency) : ''
  var lineAmount = line.amountExcludingTax ? formatAmount(line.amountExcludingTax.value, line.amountExcludingTax.currency) : ''

  return (
    '<div class="wc-quote-draft-line">' +
      '<div class="wc-quote-draft-line-main">' +
        '<strong>' + escapeHtml(line.label) + '</strong>' +
        productMeta +
        comment +
      '</div>' +
      '<div class="wc-quote-draft-line-values">' +
        '<span>Qté ' + escapeHtml(formatQuantity(line.quantity)) + '</span>' +
        '<span>' + escapeHtml(unitAmount) + '</span>' +
        '<strong>' + escapeHtml(lineAmount) + '</strong>' +
      '</div>' +
    '</div>'
  )
}

function renderAmountRow(input) {
  var formatted = input.amount ? formatAmount(input.amount.value, input.amount.currency) : ''
  var value = formatted
    ? '<strong>' + escapeHtml(formatted) + '</strong>'
    : '<span class="wc-quote-draft-amount-missing">' + escapeHtml(input.unavailableLabel) + '</span>'
  return (
    '<div class="' + escapeAttributeValue(input.className) + '">' +
      '<span>' + escapeHtml(input.label) + '</span>' +
      value +
    '</div>'
  )
}

function renderContextRow(label, record) {
  return (
    '<div class="wc-quote-draft-context-row">' +
      '<span>' + escapeHtml(label) + '</span>' +
      '<strong>' + escapeHtml(record.label || fallbackRecordLabel(record.objectType)) + '</strong>' +
    '</div>'
  )
}

function normalizeDraft(value) {
  if (!value || typeof value !== 'object') return null
  return {
    id: String(value.id || '').trim(),
    status: String(value.status || 'draft').trim(),
    customer: normalizeRecord(value.customer),
    deal: normalizeRecord(value.deal),
    sections: normalizeSections(value.sections),
    totals: value.totals && typeof value.totals === 'object' ? value.totals : {},
    provenance: value.provenance && typeof value.provenance === 'object' ? value.provenance : {}
  }
}

function normalizeRecord(value) {
  if (!value || typeof value !== 'object') return null
  var objectType = String(value.objectType || '').trim()
  if (!objectType) return null
  return {
    objectType: objectType,
    recordId: String(value.recordId || '').trim(),
    externalId: String(value.externalId || '').trim(),
    sourceApp: String(value.sourceApp || '').trim(),
    label: String(value.label || '').trim()
  }
}

function normalizeSections(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (section) {
    if (!section || typeof section !== 'object') return null
    return {
      id: String(section.id || '').trim(),
      title: String(section.title || 'Section principale').trim(),
      lines: Array.isArray(section.lines) ? section.lines.map(normalizeLine).filter(Boolean) : []
    }
  }).filter(Boolean)
}

function normalizeLine(value) {
  if (!value || typeof value !== 'object') return null
  var product = value.product && typeof value.product === 'object' ? value.product : {}
  return {
    id: String(value.id || '').trim(),
    type: String(value.type || 'product').trim(),
    label: String(value.label || product.label || '').trim(),
    product: {
      productId: String(product.productId || '').trim(),
      label: String(product.label || '').trim(),
      reference: String(product.reference || '').trim()
    },
    comment: String(value.comment || '').trim(),
    quantity: normalizeNumber(value.quantity, 1),
    unitAmountExcludingTax: normalizeAmount(value.unitAmountExcludingTax),
    amountExcludingTax: normalizeAmount(value.amountExcludingTax)
  }
}

function normalizeAmount(value) {
  if (!value || typeof value !== 'object') return null
  if (value.value === null || value.value === undefined || value.value === '') return null
  var amount = Number(value.value)
  if (!Number.isFinite(amount)) return null
  return {
    value: amount,
    currency: String(value.currency || '').trim()
  }
}

function normalizeDialogue(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (entry) {
    if (!entry || typeof entry !== 'object') return null
    var text = String(entry.text || '').trim()
    if (!text) return null
    return {
      role: entry.role === 'user' ? 'user' : 'assistant',
      text: text
    }
  }).filter(Boolean)
}

function normalizeSubmissionResult(value) {
  if (!value || typeof value !== 'object') return null
  return {
    status: String(value.status || 'failed').trim(),
    remoteApp: String(value.remoteApp || '').trim(),
    remoteId: String(value.remoteId || '').trim(),
    remoteUrl: String(value.remoteUrl || '').trim(),
    message: String(value.message || '').trim()
  }
}

function normalizeNumber(value, fallback) {
  var parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function formatQuantity(value) {
  return Number.isInteger(value) ? String(value) : String(value).replace('.', ',')
}

function formatAmount(value, currency) {
  var amount = Number(value)
  if (!Number.isFinite(amount)) return ''
  var suffix = currency ? ' ' + currency : ''
  return amount.toLocaleString('fr-FR', {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }) + suffix
}

function normalizeLabels(value) {
  value = value && typeof value === 'object' ? value : {}
  return {
    capability: String(value.capability || '@Devis'),
    dialogueTitle: String(value.dialogueTitle || 'Dialogue'),
    draftTitle: String(value.draftTitle || 'Devis en préparation'),
    customer: String(value.customer || 'Client'),
    deal: String(value.deal || 'Opportunité'),
    noContext: String(value.noContext || 'Contexte à préciser'),
    emptyDraft: String(value.emptyDraft || 'Aucune ligne pour le moment'),
    sectionTotal: String(value.sectionTotal || 'Sous-total HT'),
    total: String(value.total || 'Total HT'),
    amountUnavailable: String(value.amountUnavailable || 'Montant HT incomplet'),
    totalUnavailable: String(value.totalUnavailable || 'Total HT incomplet'),
    submit: String(value.submit || 'Envoyer vers votre application'),
    cancel: String(value.cancel || 'Annuler')
  }
}

function calculateSectionTotal(section) {
  if (!section || !Array.isArray(section.lines) || section.lines.length === 0) return null
  if (section.lines.some(function (line) { return !line.amountExcludingTax })) return null

  var currencies = section.lines.map(function (line) {
    return line.amountExcludingTax.currency || ''
  }).filter(function (currency, index, values) {
    return values.indexOf(currency) === index
  })
  if (currencies.length > 1) return null

  return {
    value: roundAmount(section.lines.reduce(function (sum, line) {
      return sum + Number(line.amountExcludingTax.value || 0)
    }, 0)),
    currency: currencies[0] || ''
  }
}

function roundAmount(value) {
  return Math.round(Number(value) * 100) / 100
}

function fallbackRecordLabel(objectType) {
  if (objectType === 'deal') return 'Opportunité associée'
  if (objectType === 'contact') return 'Contact associé'
  return 'Client associé'
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

export {
  bindQuoteDraftUi,
  normalizeQuoteDraftUi,
  renderQuoteDraftUi
}
